import { HugeiconsIcon } from "@hugeicons/react"
import {
  AiSearchIcon,
  AlertCircleIcon,
  CheckmarkCircle02Icon,
  Loading03Icon,
} from "@hugeicons/core-free-icons"
import { Link } from "react-router-dom"
import { cn } from "@/lib/utils"
import type { ToolCallData } from "../../types/assistant.types"

interface ToolCallUIProps {
  data: ToolCallData
}

/** Inline chip rendered in the assistant bubble while the agent is
 *  fetching a referenced entity via a tool call. The chip starts in
 *  ``in_flight`` (spinner + "Reading…") and flips to ``done`` (checkmark)
 *  or ``error`` (warning) when ``tool_call_done`` arrives.
 *
 *  When the chip ends in ``done`` and points at an entity the user can
 *  open in the app, it links to the underlying detail page so the
 *  reader can dig in. Failed tool calls don't link — the chip is just
 *  a "we tried but couldn't read this" trail. */
export function ToolCallUI({ data }: ToolCallUIProps) {
  const status = data.status
  const label = formatToolCallLabel(data)
  const linkHref = status === "done" ? toolCallLinkHref(data) : null

  const Icon =
    status === "in_flight"
      ? Loading03Icon
      : status === "error"
        ? AlertCircleIcon
        : CheckmarkCircle02Icon

  const tone =
    status === "in_flight"
      ? "text-muted-foreground ring-border"
      : status === "error"
        ? "text-amber-700 ring-amber-200 bg-amber-50"
        : "text-emerald-700 ring-emerald-200 bg-emerald-50/60"

  const body = (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md bg-background px-1.5 py-0.5 text-xs ring-1",
        tone,
      )}
    >
      <HugeiconsIcon
        icon={Icon}
        size={12}
        className={cn("shrink-0", status === "in_flight" && "animate-spin")}
      />
      {status === "in_flight" && (
        <HugeiconsIcon
          icon={AiSearchIcon}
          size={12}
          className="shrink-0 -ml-0.5 text-muted-foreground"
          aria-hidden
        />
      )}
      <span className="truncate" title={label}>
        {label}
      </span>
    </span>
  )

  if (linkHref) {
    return (
      <span className="mr-1 inline-flex align-middle">
        <Link to={linkHref} className="hover:opacity-80">
          {body}
        </Link>
      </span>
    )
  }

  return <span className="mr-1 inline-flex align-middle">{body}</span>
}

function formatToolCallLabel(data: ToolCallData): string {
  const verb = data.status === "in_flight" ? "Reading" : data.status === "error" ? "Couldn't read" : "Read"
  if (data.name === "get_order") {
    const orderId = numericArg(data.args, "order_id")
    return orderId ? `${verb} Order #${orderId}` : `${verb} order`
  }
  if (data.name === "get_line_item") {
    const itemId = numericArg(data.args, "line_item_id")
    return itemId ? `${verb} line item L${itemId}` : `${verb} line item`
  }
  return `${verb} ${data.name}`
}

function toolCallLinkHref(data: ToolCallData): string | null {
  if (data.name === "get_order") {
    const orderId = numericArg(data.args, "order_id")
    return orderId ? `/orders/${orderId}` : null
  }
  // ``get_line_item`` doesn't carry the parent order id in args — without
  // it we can't link to a detail page. Leave it as a plain chip.
  return null
}

function numericArg(args: Record<string, unknown>, key: string): number | null {
  const v = args[key]
  if (typeof v === "number" && Number.isFinite(v)) return v
  if (typeof v === "string") {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}
